import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    UserCircle, PencilSimple, Envelope, Phone, Globe, Sun, Moon, DesktopTower,
    Bell, ShieldCheck, LockKey, Trash, Question, EnvelopeSimple,
    SignOut, CheckCircle, FileText, Warning, IdentificationCard,
} from "@phosphor-icons/react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

const LANGS = [
    { code: "en", label: "English", flag: "🇺🇸" },
    { code: "ar", label: "العربية", flag: "🇸🇦" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
];

export default function Account() {
    const { user, logout, refresh } = useAuth();
    const { t, lang, setLang, theme, setTheme } = useI18n();

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,8%)] text-paw-ink dark:text-paw-bone">
            <Nav />
            <main className="pt-24 sm:pt-28 pb-24 max-w-3xl mx-auto px-4 sm:px-6" data-testid="account-page">
                <ProfileHeader user={user} t={t} />

                <PersonalInformationSection user={user} t={t} onSaved={refresh} />

                <PreferencesSection
                    t={t}
                    lang={lang}
                    setLang={setLang}
                    theme={theme}
                    setTheme={setTheme}
                    notificationsEnabled={user?.notifications_enabled !== false}
                    onSaved={refresh}
                />

                <PrivacySection user={user} t={t} onDeleted={logout} />

                <HelpLegalSection t={t} />

                {/* Sign out */}
                <div className="mt-10 flex justify-center">
                    <button
                        onClick={logout}
                        data-testid="account-logout-btn"
                        className="inline-flex items-center gap-2 rounded-full border border-red-200 dark:border-red-800/50 bg-white dark:bg-transparent text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-6 py-3 text-sm font-semibold transition"
                    >
                        <SignOut size={16} weight="bold" />
                        <span>{t("account.logout")}</span>
                    </button>
                </div>
            </main>
            <Footer />
        </div>
    );
}

/* ---------- Section wrapper ---------- */
function Section({ title, description, icon, children, testId, id }) {
    return (
        <section id={id} className="mt-8 scroll-mt-24" data-testid={testId}>
            <div className="flex items-center gap-2.5 mb-3">
                {icon && (
                    <span className="w-8 h-8 rounded-xl bg-paw-forest/10 dark:bg-paw-terra/15 text-paw-forest dark:text-paw-terra grid place-items-center flex-shrink-0">
                        {icon}
                    </span>
                )}
                <div>
                    <h2 className="font-display text-lg sm:text-xl tracking-tight text-paw-ink dark:text-paw-bone">
                        {title}
                    </h2>
                    {description && (
                        <p className="text-xs text-paw-ink2 dark:text-paw-bone/60">{description}</p>
                    )}
                </div>
            </div>
            <div className="card-soft p-5 sm:p-6 space-y-5">{children}</div>
        </section>
    );
}

/* ---------- Profile header ---------- */
function ProfileHeader({ user, t }) {
    const [imgFailed, setImgFailed] = useState(false);
    const initials = (user?.name || user?.email || "?")
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((w) => w.charAt(0))
        .join("")
        .toUpperCase() || "?";
    const showImage = !!user?.picture && !imgFailed;

    return (
        <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-paw-forest to-paw-forestDark dark:from-paw-forestDark dark:to-[hsl(152,25%,10%)] text-white p-6 sm:p-8" data-testid="account-header">
            <div aria-hidden className="absolute -top-16 -end-10 w-56 h-56 rounded-full bg-paw-terra/25 blur-3xl" />
            <div aria-hidden className="absolute -bottom-16 -start-10 w-64 h-64 rounded-full bg-white/8 blur-3xl" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-5">
                <div
                    className="relative w-20 h-20 rounded-full ring-2 ring-white/30 shadow-hover overflow-hidden flex-shrink-0 bg-gradient-to-br from-paw-terra to-paw-terraDark grid place-items-center"
                    data-testid="account-avatar"
                >
                    {showImage ? (
                        <img
                            src={user.picture}
                            alt={user.name || user.email}
                            referrerPolicy="no-referrer"
                            onError={() => setImgFailed(true)}
                            className="absolute inset-0 w-full h-full object-cover"
                            data-testid="account-avatar-img"
                        />
                    ) : (
                        <span
                            className="font-display text-3xl text-white select-none"
                            data-testid="account-avatar-initials"
                        >
                            {initials}
                        </span>
                    )}
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-terra/95">
                        {t("account.eyebrow")}
                    </p>
                    <h1
                        data-testid="account-heading"
                        className="mt-1 font-display text-2xl sm:text-3xl tracking-tight leading-tight truncate"
                    >
                        {user?.name || user?.email}
                    </h1>
                    <p className="mt-1 text-sm text-white/75 truncate inline-flex items-center gap-1.5">
                        <Envelope size={13} weight="fill" /> {user?.email}
                    </p>
                </div>
                <a
                    href="#personal-info"
                    data-testid="account-edit-profile"
                    className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 px-4 py-2.5 text-sm font-semibold whitespace-nowrap self-start sm:self-auto"
                >
                    <PencilSimple size={14} weight="bold" />
                    {t("account.header_edit")}
                </a>
            </div>
        </div>
    );
}

/* ---------- Personal information ---------- */
function PersonalInformationSection({ user, t, onSaved }) {
    const [form, setForm] = useState({ name: "", phone: "", country: "" });
    const [status, setStatus] = useState("idle"); // idle | saving | saved
    const isOAuth = user?.provider === "google";

    useEffect(() => {
        if (!user) return;
        setForm({
            name: user.name || "",
            phone: user.phone || "",
            country: user.country || "",
        });
    }, [user?.id, user?.name, user?.phone, user?.country]);

    const submit = async (e) => {
        e.preventDefault();
        setStatus("saving");
        try {
            // Do not attempt to change name for OAuth users
            const payload = isOAuth
                ? { phone: form.phone, country: form.country }
                : form;
            await api.patch("/me/profile", payload);
            await onSaved?.();
            setStatus("saved");
            setTimeout(() => setStatus("idle"), 1800);
        } catch {
            setStatus("idle");
        }
    };

    return (
        <Section
            title={t("account.section_profile")}
            icon={<IdentificationCard size={18} weight="fill" />}
            testId="account-section-personal"
        >
            <form id="personal-info" onSubmit={submit} className="space-y-4">
                <Field
                    label={t("account.f_name")}
                    icon={<UserCircle size={16} weight="duotone" />}
                    hint={isOAuth ? t("account.f_name_hint_google") : undefined}
                    testId="account-field-name"
                >
                    <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={t("account.f_name_ph")}
                        data-testid="account-input-name"
                        readOnly={isOAuth}
                        disabled={isOAuth}
                        className={`input-soft${isOAuth ? " opacity-70 cursor-not-allowed" : ""}`}
                    />
                </Field>

                <Field
                    label={t("account.f_email")}
                    icon={<Envelope size={16} weight="duotone" />}
                    hint={t("account.f_email_hint")}
                    testId="account-field-email"
                >
                    <input
                        type="email"
                        value={user?.email || ""}
                        readOnly
                        disabled
                        data-testid="account-input-email"
                        className="input-soft opacity-70 cursor-not-allowed"
                    />
                </Field>

                <Field
                    label={t("account.f_phone")}
                    icon={<Phone size={16} weight="duotone" />}
                    testId="account-field-phone"
                >
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder={t("account.f_phone_ph")}
                        data-testid="account-input-phone"
                        className="input-soft"
                        dir="ltr"
                    />
                </Field>

                <Field
                    label={t("account.f_country")}
                    icon={<Globe size={16} weight="duotone" />}
                    testId="account-field-country"
                >
                    <input
                        type="text"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                        placeholder={t("account.f_country_ph")}
                        data-testid="account-input-country"
                        className="input-soft"
                    />
                </Field>

                <div className="flex items-center gap-3 pt-2">
                    <button
                        type="submit"
                        data-testid="account-save-profile"
                        disabled={status === "saving"}
                        className="btn-primary text-sm"
                    >
                        {status === "saving" ? t("account.saving") : t("account.save")}
                    </button>
                    {status === "saved" && (
                        <span
                            data-testid="account-save-ok"
                            className="inline-flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400 font-semibold"
                        >
                            <CheckCircle size={16} weight="fill" /> {t("account.saved")}
                        </span>
                    )}
                </div>
            </form>
        </Section>
    );
}

function Field({ label, icon, hint, testId, children }) {
    return (
        <label className="block" data-testid={testId}>
            <span className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 inline-flex items-center gap-1.5">
                {icon}{label}
            </span>
            <div className="mt-1.5">{children}</div>
            {hint && <span className="mt-1 block text-[11px] text-paw-ink2 dark:text-paw-bone/50">{hint}</span>}
        </label>
    );
}

/* ---------- App preferences ---------- */
function PreferencesSection({ t, lang, setLang, theme, setTheme, notificationsEnabled, onSaved }) {
    const [notif, setNotif] = useState(notificationsEnabled);

    useEffect(() => { setNotif(notificationsEnabled); }, [notificationsEnabled]);

    const toggleNotif = async () => {
        const next = !notif;
        setNotif(next);
        try {
            await api.post("/me/preferences", { notifications_enabled: next });
            await onSaved?.();
        } catch {
            setNotif(!next); // revert
        }
    };

    return (
        <Section
            title={t("account.section_prefs")}
            icon={<Globe size={18} weight="fill" />}
            testId="account-section-prefs"
        >
            {/* Language */}
            <div data-testid="account-pref-language">
                <p className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-2">
                    {t("account.pref_language")}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            type="button"
                            onClick={() => setLang(l.code)}
                            data-testid={`account-lang-${l.code}`}
                            aria-pressed={lang === l.code}
                            className={`flex items-center gap-2 rounded-2xl border px-3.5 py-3 text-sm transition ${
                                lang === l.code
                                    ? "border-paw-forest dark:border-paw-terra bg-paw-forest/5 dark:bg-paw-terra/10 text-paw-ink dark:text-paw-bone font-semibold"
                                    : "border-paw-line dark:border-white/10 text-paw-ink2 dark:text-paw-bone/70 hover:border-paw-forest/40 dark:hover:border-paw-terra/40"
                            }`}
                        >
                            <span aria-hidden className="text-base">{l.flag}</span>
                            <span className="truncate">{l.label}</span>
                            {lang === l.code && <CheckCircle size={14} weight="fill" className="ms-auto text-paw-forest dark:text-paw-terra" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* Appearance */}
            <div data-testid="account-pref-theme">
                <p className="text-xs uppercase tracking-wider font-semibold text-paw-ink2 dark:text-paw-bone/60 mb-2">
                    {t("account.pref_appearance")}
                </p>
                <div className="grid grid-cols-3 gap-2">
                    <ThemeCard active={theme === "light"} onClick={() => setTheme("light")} icon={<Sun size={18} weight="fill" />} label={t("theme.light")} testId="account-theme-light" />
                    <ThemeCard active={theme === "dark"} onClick={() => setTheme("dark")} icon={<Moon size={18} weight="fill" />} label={t("theme.dark")} testId="account-theme-dark" />
                    <ThemeCard active={theme === "system"} onClick={() => setTheme("system")} icon={<DesktopTower size={18} weight="fill" />} label={t("theme.system")} testId="account-theme-system" />
                </div>
                <p className="mt-2 text-[11px] text-paw-ink2 dark:text-paw-bone/50">{t("account.pref_appearance_hint")}</p>
            </div>

            {/* Notifications */}
            <div className="flex items-start justify-between gap-4 pt-2" data-testid="account-pref-notif">
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone inline-flex items-center gap-2">
                        <Bell size={14} weight="fill" /> {t("account.pref_notifications")}
                    </p>
                    <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 mt-0.5">{t("account.pref_notifications_hint")}</p>
                </div>
                <button
                    type="button"
                    role="switch"
                    aria-checked={notif}
                    onClick={toggleNotif}
                    data-testid="account-notif-toggle"
                    className={`relative w-12 h-7 flex-shrink-0 rounded-full transition ${notif ? "bg-paw-forest dark:bg-paw-terra" : "bg-paw-line dark:bg-white/15"}`}
                >
                    <span
                        aria-hidden
                        className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${notif ? "translate-x-5 rtl:-translate-x-5" : "translate-x-0.5 rtl:-translate-x-0.5"}`}
                    />
                </button>
            </div>
        </Section>
    );
}

function ThemeCard({ active, onClick, icon, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            aria-pressed={active}
            className={`flex flex-col items-center justify-center gap-1.5 rounded-2xl border py-3 px-2 text-xs sm:text-sm font-semibold transition ${
                active
                    ? "border-paw-forest dark:border-paw-terra bg-paw-forest/5 dark:bg-paw-terra/10 text-paw-forest dark:text-paw-terra"
                    : "border-paw-line dark:border-white/10 text-paw-ink2 dark:text-paw-bone/70 hover:border-paw-forest/40 dark:hover:border-paw-terra/40"
            }`}
        >
            {icon}
            <span>{label}</span>
        </button>
    );
}

/* ---------- Privacy & security ---------- */
function PrivacySection({ user, t, onDeleted }) {
    const hasPassword = user?.has_password;

    return (
        <Section
            title={t("account.section_privacy")}
            icon={<LockKey size={18} weight="fill" />}
            testId="account-section-privacy"
            id="privacy"
        >
            {/* Public pet profile explanation */}
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 dark:bg-emerald-500/10 p-4">
                <p className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                    <ShieldCheck size={16} weight="fill" /> {t("account.priv_public_h")}
                </p>
                <p className="mt-1.5 text-sm text-paw-ink dark:text-paw-bone/85">{t("account.priv_public_sub")}</p>
                <Link
                    to="/pets"
                    data-testid="account-privacy-manage-pets"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-paw-forest dark:text-paw-terra hover:underline"
                >
                    {t("account.priv_public_manage")} →
                </Link>
            </div>

            {/* Community Map Visibility — Hidden (default) | Visible approximately.
                Wired to PATCH /api/community/settings visibility. Server never
                exposes exact coordinates or contact details regardless. */}
            <CommunityVisibilitySection t={t} user={user} />

            {/* Change password */}
            <div className="pt-2" data-testid="account-privacy-password">
                <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone">{t("account.priv_password_h")}</p>
                {hasPassword ? (
                    <ChangePasswordForm t={t} />
                ) : (
                    <p className="mt-2 text-sm text-paw-ink2 dark:text-paw-bone/60">{t("account.priv_password_google")}</p>
                )}
            </div>

            {/* Delete account */}
            <div className="pt-2 border-t border-paw-line dark:border-white/10 mt-4" data-testid="account-privacy-delete">
                <p className="mt-4 text-sm font-semibold text-red-600 dark:text-red-400 inline-flex items-center gap-2">
                    <Warning size={16} weight="fill" /> {t("account.priv_delete_h")}
                </p>
                <p className="mt-1 text-xs text-paw-ink2 dark:text-paw-bone/60">{t("account.priv_delete_sub")}</p>
                <DeleteAccountButton t={t} onDeleted={onDeleted} />
                <p className="mt-3 text-[11px] text-paw-ink2 dark:text-paw-bone/60" data-testid="account-delete-support">
                    {t("account.priv_delete_help")}{" "}
                    <a
                        href={supportMailto({ subject: "PawLink Support Request — Delete Account" })}
                        className="text-paw-forest dark:text-paw-terra hover:underline font-medium"
                        dir="ltr"
                    >
                        {SUPPORT_EMAIL}
                    </a>
                </p>
            </div>
        </Section>
    );
}

function ChangePasswordForm({ t }) {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [msg, setMsg] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        try {
            await api.post("/me/password", { current_password: current, new_password: next });
            setCurrent("");
            setNext("");
            setMsg({ kind: "ok", text: t("account.priv_password_ok") });
        } catch (err) {
            const detail = err.response?.data?.detail;
            setMsg({ kind: "err", text: typeof detail === "string" ? detail : "Password update failed." });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="mt-3 space-y-3">
            <input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder={t("account.priv_password_current")}
                data-testid="account-password-current"
                className="input-soft"
                autoComplete="current-password"
                required
            />
            <input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder={t("account.priv_password_new")}
                data-testid="account-password-new"
                className="input-soft"
                autoComplete="new-password"
                minLength={8}
                required
            />
            <button
                type="submit"
                disabled={busy || !current || next.length < 8}
                data-testid="account-password-submit"
                className="btn-primary text-sm"
            >
                {t("account.priv_password_save")}
            </button>
            {msg && (
                <p
                    data-testid="account-password-msg"
                    className={`text-xs ${msg.kind === "ok" ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}
                >
                    {msg.text}
                </p>
            )}
        </form>
    );
}

function DeleteAccountButton({ t, onDeleted }) {
    const [open, setOpen] = useState(false);
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const canDelete = confirm === "DELETE";

    const doDelete = async () => {
        if (!canDelete) return;
        setBusy(true);
        try {
            await api.delete("/me");
            localStorage.removeItem("pawlink_token");
            await onDeleted?.();
        } catch {
            setBusy(false);
        }
    };

    if (!open) {
        return (
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid="account-delete-open"
                className="mt-3 inline-flex items-center gap-2 rounded-full border border-red-200 dark:border-red-800/50 bg-transparent text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 px-4 py-2 text-sm font-semibold transition"
            >
                <Trash size={14} weight="fill" />
                {t("account.priv_delete_btn")}
            </button>
        );
    }

    return (
        <div
            data-testid="account-delete-panel"
            className="mt-3 rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/60 dark:bg-red-900/15 p-4"
        >
            <p className="text-sm font-semibold text-red-700 dark:text-red-300">{t("account.priv_delete_confirm_h")}</p>
            <p className="text-xs text-red-600/85 dark:text-red-300/80 mt-1">{t("account.priv_delete_confirm_sub")}</p>
            <input
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder={t("account.priv_delete_placeholder")}
                data-testid="account-delete-input"
                dir="ltr"
                className="mt-3 input-soft !border-red-300 dark:!border-red-800/60 focus:!ring-red-500/30"
            />
            <div className="mt-3 flex flex-wrap gap-2">
                <button
                    type="button"
                    onClick={doDelete}
                    disabled={!canDelete || busy}
                    data-testid="account-delete-confirm"
                    className="inline-flex items-center gap-2 rounded-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white transition"
                >
                    <Trash size={14} weight="fill" />
                    {t("account.priv_delete_yes")}
                </button>
                <button
                    type="button"
                    onClick={() => { setOpen(false); setConfirm(""); }}
                    data-testid="account-delete-cancel"
                    className="inline-flex items-center gap-2 rounded-full border border-paw-line dark:border-white/15 px-4 py-2 text-sm font-semibold text-paw-ink dark:text-paw-bone hover:bg-white dark:hover:bg-white/5 transition"
                >
                    {t("account.priv_delete_cancel")}
                </button>
            </div>
        </div>
    );
}

/* ---------- Help & Legal (merged) ---------- */
function HelpLegalSection({ t }) {
    const items = useMemo(() => ([
        {
            icon: <ShieldCheck size={18} weight="duotone" />,
            label: t("account.legal_privacy"),
            to: "/privacy",
            testId: "account-link-privacy",
        },
        {
            icon: <FileText size={18} weight="duotone" />,
            label: t("account.legal_terms"),
            to: "/terms",
            testId: "account-link-terms",
        },
        {
            icon: <Question size={18} weight="duotone" />,
            label: t("account.help_faq"),
            to: "/faq",
            testId: "account-link-faq",
        },
        {
            icon: <EnvelopeSimple size={18} weight="duotone" />,
            label: t("account.help_contact"),
            to: "/support",
            testId: "account-link-support",
        },
    ]), [t]);

    return (
        <Section
            title={t("account.section_help_legal")}
            icon={<Question size={18} weight="fill" />}
            testId="account-section-help-legal"
        >
            <ul className="divide-y divide-paw-line dark:divide-white/10 -my-1">
                {items.map((l) => (
                    <li key={l.testId}>
                        <Link
                            to={l.to}
                            data-testid={l.testId}
                            className="flex items-center gap-3 py-3 group text-paw-ink dark:text-paw-bone hover:text-paw-forest dark:hover:text-paw-terra transition"
                        >
                            <span className="w-9 h-9 rounded-xl bg-paw-muted dark:bg-white/5 text-paw-forest dark:text-paw-terra grid place-items-center flex-shrink-0">
                                {l.icon}
                            </span>
                            <span className="text-sm font-medium flex-1">{l.label}</span>
                            <span className="text-paw-ink2 dark:text-paw-bone/40 group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 transition text-lg leading-none">›</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </Section>
    );
}

/* ---------- Community Map Visibility -------------------------------------
   Two states: "invisible" (default) or "approximate". The value is persisted
   at PATCH /api/community/settings — a preserved existing endpoint. The
   backend never exposes exact coords or personal contact details regardless
   of this toggle; this only controls whether the user appears under the
   "Nearby Community" filter on the shared map. */
function CommunityVisibilitySection({ t, user }) {
    const [value, setValue] = useState(user?.community_visibility || "invisible");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");
    const change = async (next) => {
        if (saving || next === value) return;
        setSaving(true); setErr("");
        try {
            await api.patch("/community/settings", { visibility: next });
            setValue(next);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not save.");
        } finally { setSaving(false); }
    };
    return (
        <div className="mt-3 rounded-2xl border border-paw-line dark:border-white/10 p-4" data-testid="account-community-visibility">
            <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone">
                {t("account.community_vis_h") || "Community Map Visibility"}
            </p>
            <p className="mt-1 text-xs text-paw-ink2 dark:text-paw-bone/70">
                {t("account.community_vis_sub") || "Controls whether you appear under Nearby Community on the shared map. Exact location and personal contact details are never shared."}
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                    type="button"
                    onClick={() => change("invisible")}
                    data-testid="account-community-vis-hidden"
                    aria-pressed={value === "invisible"}
                    className={"rounded-xl border h-10 text-sm font-semibold transition " +
                        (value === "invisible"
                            ? "bg-paw-terra border-paw-terra text-white"
                            : "border-paw-line dark:border-white/15 hover:bg-paw-terra/8")}
                >
                    {t("account.community_vis_hidden") || "Hidden"}
                </button>
                <button
                    type="button"
                    onClick={() => change("approximate")}
                    data-testid="account-community-vis-approximate"
                    aria-pressed={value === "approximate"}
                    className={"rounded-xl border h-10 text-sm font-semibold transition " +
                        (value === "approximate"
                            ? "bg-paw-terra border-paw-terra text-white"
                            : "border-paw-line dark:border-white/15 hover:bg-paw-terra/8")}
                >
                    {t("account.community_vis_approximate") || "Visible approximately"}
                </button>
            </div>
            {err && <p className="mt-2 text-[12px] text-red-600">{err}</p>}
        </div>
    );
}

